import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

import { useMutation } from '@tanstack/react-query';
import { useState as useReactState } from 'react';

/**
 * Represents the status and metadata of a single mutation operation within a batch.
 * Tracks the current state, result, and control functions for individual mutations.
 *
 * @template TParams - The type of parameters passed to the mutation
 * @template TResult - The type of data returned by the mutation on success
 */
export interface MutationStatus<TParams, TResult> {
  /** The parameters used to execute this mutation */
  params: TParams;
  /** Current status of the mutation: 'idle', 'pending', 'success', or 'error' */
  status: UseMutationResult['status'];
  /** The successful result data when status is 'success' */
  data?: TResult;
  /** The error object when status is 'error' */
  error?: Error;
  /** Optional function to abort the mutation */
  abort?: () => void;
  /** Function to retry the mutation with the original parameters */
  retry?: () => void;
  /** Optional progress indicator (0-100) for long-running mutations */
  progress?: number;
  /** Whether the mutation is currently in progress */
  isPending: boolean;
  /** Whether the mutation encountered an error */
  isError: boolean;
  /** Whether the mutation completed successfully */
  isSuccess: boolean;
}

/**
 * Configuration options for the useBatchMutation hook.
 * Extends standard React Query mutation options with additional batch state management.
 *
 * @template TResult - The type of data returned by the mutation on success
 * @template Error - The type of error thrown by the mutation
 * @template TParams - The type of parameters passed to the mutation
 * @template TContext - The type of context used in onMutate callback
 */
export type BatchMutationProps<TResult, Error, TParams, TContext> = UseMutationOptions<TResult, Error, TParams, TContext> & {
  /** Optional external state array for managing mutation statuses */
  state?: MutationStatus<TParams, TResult>[];
  /** Optional state setter function for external state management */
  setState?: React.Dispatch<React.SetStateAction<MutationStatus<TParams, TResult>[]>>;
};

/**
 * Return type of the useBatchMutation hook.
 * Combines standard React Query mutation results with batch-specific utilities.
 *
 * @template TResult - The type of data returned by the mutation on success
 * @template Error - The type of error thrown by the mutation
 * @template TParams - The type of parameters passed to the mutation
 * @template TContext - The type of context used in onMutate callback
 */
interface UseBatchMutationReturn<TResult, Error, TParams, TContext> extends Omit<UseMutationResult<TResult, Error, TParams, TContext>, 'reset'> {
  /**
   * Resets both the mutation state and all tracked mutation statuses.
   * Clears any pending, successful, or failed mutations.
   */
  reset: () => void;
  /**
   * Executes multiple mutations in parallel with optional state clearing.
   *
   * @param paramsArray - Array of parameters for each mutation to execute
   * @param options - Configuration options for batch execution
   * @param options.clearStatuses - Whether to clear previous statuses before execution (default: true)
   * @returns Promise resolving to separate arrays of successful and failed mutation results
   */
  executeMutations: (paramsArray: TParams[], options?: { clearStatuses?: boolean }) => Promise<{ fulfilledResults: TResult[]; rejectedResults: Error[] }>;
  /**
   * Clears all stored mutation statuses without affecting pending mutations.
   */
  clearStatuses: () => void;
  /** Array of current mutation statuses tracking individual mutation states */
  statuses: MutationStatus<TParams, TResult>[];
}

/**
 * React hook for managing multiple mutations in batch operations.
 *
 * This hook provides a convenient way to execute multiple mutations in parallel,
 * track their individual statuses, and handle both success and error cases.
 * It maintains an array of mutation statuses that can be used to display progress,
 * retry failed mutations, or access results individually.
 *
 * @template TResult - The type of data returned by each mutation on success
 * @template Error - The type of error thrown by mutations
 * @template TParams - The type of parameters passed to each mutation
 * @template TContext - The type of context used in onMutate callback
 *
 * @param batchMutationProps - Configuration object extending UseMutationOptions
 * @returns Object containing mutation controls, batch execution method, and status tracking
 *
 * @example
 * ```ts
 * const { executeMutations, statuses, reset } = useBatchMutation({
 *   mutationFn: async (id: number) => api.deleteItem(id),
 *   onSuccess: (data) => console.log('Item deleted:', data),
 * });
 *
 * const handleDeleteMultiple = async (ids: number[]) => {
 *   const { fulfilledResults, rejectedResults } = await executeMutations(ids);
 *   console.log(`Deleted ${fulfilledResults.length}, failed: ${rejectedResults.length}`);
 * };
 * ```
 */
export function useBatchMutation<TResult, Error, TParams, TContext>(
  batchMutationProps: BatchMutationProps<TResult, Error, TParams, TContext>
): UseBatchMutationReturn<TResult, Error, TParams, TContext> {
  const [internalStatus, internalSetStatus] = useReactState<MutationStatus<TParams, TResult>[]>([]);

  const { mutationFn, onMutate, onSuccess, onError, state, setState, ...props } = batchMutationProps;

  const statuses = state ?? internalStatus;
  const setStatuses = setState ?? internalSetStatus;

  const mutation = useMutation<TResult, Error, TParams, TContext>({
    mutationFn,
    onMutate: (params, context) => {
      const retry = () => {
        mutation.mutate(params);
      };
      const reset = async () => {
        mutation.reset();
      };
      setStatuses((prev) => {
        const existingIndex = prev.findIndex((status) => status.params === params);
        if (existingIndex !== -1) {
          const updatedStatus = {
            ...prev[existingIndex],
            status: 'pending',
            isPending: true,
            isError: false,
            isSuccess: false,
            retry,
            reset,
          } as MutationStatus<TParams, TResult>;
          return [...prev.slice(0, existingIndex), updatedStatus, ...prev.slice(existingIndex + 1)];
        } else {
          return [
            ...prev,
            {
              params,
              status: 'pending',
              isPending: true,
              isError: false,
              isSuccess: false,
              retry,
              reset,
            },
          ];
        }
      });

      if (onMutate) {
        return onMutate(params, context);
      }
      return context as TContext;
    },
    onSuccess: (data, params, onMutateResult, context) => {
      setStatuses((prev) =>
        prev.map((status) =>
          status.params === params
            ? {
                ...status,
                status: 'success',
                data,
                isPending: false,
                isError: false,
                isSuccess: true,
              }
            : status
        )
      );
      onSuccess?.(data, params, onMutateResult, context);
    },
    onError: (error, params, onMutateResult, context) => {
      setStatuses((prev) =>
        prev.map((status) =>
          status.params === params
            ? ({
                ...status,
                status: 'error',
                error,
                isPending: false,
                isError: true,
                isSuccess: false,
              } as MutationStatus<TParams, TResult>)
            : status
        )
      );
      onError?.(error, params, onMutateResult, context);
    },
    ...props,
  });

  const executeMutations = async (paramsArray: TParams[], options?: { clearStatuses?: boolean }) => {
    const { clearStatuses = true } = options || {};

    if (clearStatuses) {
      setStatuses([]);
    }
    const mutationPromises = paramsArray.map((params) => mutation.mutateAsync(params));

    const results = await Promise.allSettled(mutationPromises);

    const fulfilledResults = results.filter((result) => result.status === 'fulfilled').map((result) => (result as PromiseFulfilledResult<TResult>).value);

    const rejectedResults = results.filter((result) => result.status === 'rejected').map((result) => (result as PromiseRejectedResult).reason);

    return { fulfilledResults, rejectedResults };
  };

  const clearStatuses = () => {
    setStatuses([]);
  };

  // override reset function
  const reset = () => {
    mutation.reset();
    setStatuses([]);
  };

  return {
    ...mutation,
    reset,
    executeMutations,
    clearStatuses,
    statuses,
  };
}
